import {useIntl} from '@pinecast/i18n';
import type {CSS} from '@pinecast/styles';
import styled, {useColors} from '@pinecast/styles';
import * as React from 'react';

import {DEFAULT_FONT} from './constants';
import useForceUpdate from './hooks/useForceUpdate';
import {useMountEffect} from './hooks/useMountEffect';
import {usePreviousValue} from './hooks/usePreviousValue';
import useToggle from './hooks/useToggle';
import IconButton from './IconButton';
import * as MenuIcons from './icons/menus';
import {CloseableLayer} from './Layer';
import {Positioner} from './Positioner';
import type {ElementConfig, LocalizedReactNode} from './types';

const MenuWrapper = styled(
  'menu',
  (
    {
      'aria-hidden': ariaHidden,
      $xAlign,
      $yAlign,
    }: {
      'aria-hidden': boolean;
      $xAlign: 'left' | 'right';
      $yAlign: 'top' | 'bottom';
    },
    designColors,
  ) => ({
    backgroundColor: designColors.contentBackground,
    borderColor: designColors.borderGrayNormal,
    borderStyle: 'solid',
    borderTopWidth: '1px',
    borderBottomWidth: '2px',
    borderLeftWidth: '1px',
    borderRightWidth: '1px',
    borderRadius: 4,
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15), 0 5px 15px rgba(0, 0, 0, 0.125)',
    display: 'inline-flex',
    flexDirection: 'column',
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    maxHeight: 300,
    minWidth: 200,
    opacity: ariaHidden ? 0 : 1,
    overflowY: 'auto',
    padding: '4px 0',
    pointerEvents: ariaHidden ? 'none' : 'auto',
    transform: ariaHidden ? 'scale(0.9)' : 'scale(1)',
    transformOrigin: `${$xAlign === 'left' ? '20%' : '80%'} ${
      $yAlign === 'top' ? 'bottom' : 'top'
    }`,
    transition: 'opacity 0.2s, transform 0.2s',
    zIndex: 11,
  }),
);

const MenuOptionRow = styled(
  'button',
  (
    {
      'aria-selected': selected,
      $destructive,
    }: {
      'aria-selected': boolean;
      $destructive: boolean;
    },
    designColors,
  ): CSS => ({
    WebkitAppearance: 'none',
    background: selected
      ? $destructive
        ? designColors.badgeRedBackground
        : designColors.accentBlueLight
      : 'none',
    border: 0,
    borderRadius: 3,
    color: $destructive ? designColors.badgeRedText : designColors.contentText,
    cursor: 'pointer',
    flex: '0 0',
    fontFamily: DEFAULT_FONT,
    fontSize: 14,
    margin: '0 4px',
    padding: 8,
    textAlign: 'left',
    whiteSpace: 'nowrap',
  }),
  {role: 'option'},
);

export type MenuOption<T> = {
  name: LocalizedReactNode;
  slug: T;
  destructive?: boolean;
};

type Props<T> = {
  children: React.ReactNode;
  id?: string;
  options: Array<MenuOption<T> | null | false>;
  open: boolean;
  onSelect: (slug: T) => void;
  onClose: () => void;
  toSelect?: T;
  wrapperStyle?: React.CSSProperties;
};

const ContextMenu = <T extends string | number>(props: Props<T>) => {
  const {
    children,
    id,
    onClose,
    onSelect,
    open,
    options,
    toSelect,
    wrapperStyle,
  } = props;

  const wrapper = React.useRef<HTMLElement | null>(null);
  const [selectionIndex, setSelectionIndex] = React.useState(0);
  const forceUpdate = useForceUpdate();

  const selectSelected = React.useCallback(() => {
    setTimeout(() => {
      (
        wrapper.current?.querySelector(
          '[aria-selected=true]',
        ) as HTMLElement | null
      )?.focus();
    }, 0);
  }, []);

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!open) {
        return;
      }
      if (e.keyCode === 27) {
        // ESC
        onClose();
        return;
      }
      e.preventDefault();
      e.stopPropagation();

      if (e.keyCode === 38) {
        // up arrow
        const index = selectionIndex - 1;
        setSelectionIndex(index < 0 ? options.length - 1 : index);
        selectSelected();
      } else if (e.keyCode === 40) {
        // down arrow
        const index = selectionIndex + 1;
        setSelectionIndex(index === options.length ? 0 : index);
        selectSelected();
      } else if (e.keyCode === 13) {
        // enter
        const item = options[selectionIndex];
        if (!item) {
          return;
        }
        onSelect(item.slug);
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [onClose, onSelect, open, options, selectSelected, selectionIndex]);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (open) {
        onClose();
      }
    };
    document.body.addEventListener('click', handler, true);
    return () => {
      document.body.removeEventListener('click', handler, true);
    };
  }, [open, onClose]);

  const prevOpen = usePreviousValue(open);
  React.useEffect(() => {
    if (open && !prevOpen) {
      const found = options.findIndex(x => !!x && x.slug === toSelect);
      setSelectionIndex(found === -1 ? 0 : found);
      selectSelected();
    }
  }, [open, options, prevOpen, selectSelected, toSelect]);

  useMountEffect(() => forceUpdate());

  const renderOption = React.useCallback(
    (option: MenuOption<T> | false | null, i: number) => {
      if (!option) {
        return null;
      }
      const {name, slug, destructive} = option;
      return (
        <MenuOptionRow
          $destructive={destructive || false}
          aria-selected={selectionIndex === i}
          id={`${id}__${String(slug)}`}
          role="option"
          key={String(slug)}
          onClick={(e: React.SyntheticEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();
            onSelect(slug);
            onClose();
          }}
          onMouseEnter={() => {
            setSelectionIndex(i);
            selectSelected();
          }}
          tabIndex={open ? 0 : -1}
        >
          {name}
        </MenuOptionRow>
      );
    },
    [id, onClose, onSelect, open, selectSelected, selectionIndex],
  );

  return (
    <Positioner content={<>{children}</>} preferY="bottom" style={wrapperStyle}>
      {React.useCallback(
        (ref, styles, xAlign, yAlign) => (
          <CloseableLayer onClose={onClose} pointerEvents={open}>
            <div
              ref={ref}
              style={{
                ...styles,
                pointerEvents: 'none',
              }}
            >
              <MenuWrapper
                $xAlign={xAlign}
                $yAlign={yAlign}
                ref={wrapper}
                aria-hidden={!open}
                aria-activedescendant={
                  id && options[selectionIndex]
                    ? `${id}__${String(
                        (options[selectionIndex] as MenuOption<T>).slug,
                      )}`
                    : undefined
                }
                id={id}
                role="listbox"
              >
                {options.map(renderOption)}
              </MenuWrapper>
            </div>
          </CloseableLayer>
        ),
        [id, onClose, open, options, renderOption, selectionIndex],
      )}
    </Positioner>
  );
};

export default ContextMenu;

type MenuProps<T extends string | number> = {
  onSelect: Props<T>['onSelect'];
  options: Props<T>['options'];
  size?: ElementConfig<typeof IconButton>['size'];
  style?: CSS;
  toSelect?: Props<T>['toSelect'];
};
const IconMenuFactory = function <T extends string | number>(
  Component: React.ComponentType<any>,
) {
  return (props: MenuProps<T>) => {
    const designColors = useColors(); // eslint-disable-line react-hooks/rules-of-hooks
    const {formatMessage} = useIntl(); // eslint-disable-line react-hooks/rules-of-hooks
    const [open, toggle] = useToggle(); // eslint-disable-line react-hooks/rules-of-hooks
    return (
      <ContextMenu<T>
        open={open}
        onClose={() => toggle(false)}
        onSelect={props.onSelect}
        options={props.options}
        toSelect={props.toSelect}
      >
        <IconButton
          Component={Component}
          iconProps={{
            color:
              (props.style && props.style.color && String(props.style.color)) ||
              designColors.contentText,
            size: 20,
          }}
          onClick={toggle}
          size={props.size}
          style={props.style}
          title={formatMessage({
            description: 'Label for toggling a menu button',
            defaultMessage: 'Toggle menu',
          })}
        />
      </ContextMenu>
    );
  };
};

export const KebabIconMenu = IconMenuFactory(MenuIcons.Kebab);
export const MeatballIconMenu = IconMenuFactory(MenuIcons.Meatball);
